import { useState, useCallback } from 'react';
import { getCSRFToken } from '../utils/csrf';
import type { ApiResponse } from '../types/organization';

async function apiFetch(url: string, options: RequestInit = {}): Promise<any> {
    const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-XSRF-TOKEN': getCSRFToken(),
            ...(options.headers || {}),
        },
        ...options,
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Error inesperado');
    }

    return data;
}

export function useOrganizationApi() {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const execute = useCallback(async <T = ApiResponse>(
        url: string,
        options: RequestInit = {}
    ): Promise<T & { success: boolean }> => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await apiFetch(url, options);
            return result;
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Error inesperado';
            setError(message);
            return { success: false, message } as T & { success: boolean };
        } finally {
            setIsLoading(false);
        }
    }, []);

    const post = useCallback((url: string, data: any) =>
        execute(url, { method: 'POST', body: JSON.stringify(data) }), [execute]);

    const put = useCallback((url: string, data: any) =>
        execute(url, { method: 'PUT', body: JSON.stringify(data) }), [execute]);

    const del = useCallback((url: string) =>
        execute(url, { method: 'DELETE' }), [execute]);

    return { post, put, del, isLoading, error, setError };
}
